<template>
  <main class="login" v-if="schoolStore.school?.school_id">
    <section>
      <div class="branding">
        <figure class="logo">
          <Logo />
        </figure>
        <div class="school-name">{{ schoolStore.school.school_name }}</div>
        <div class="dropdown">
          <button class="btn text-black dropdown-toggle text-capitalize fs-normal px-2"
          type="button"
          id="dropdownLanguageButton"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          >
          <component :is="getFlagComponent(currentLanguage.flag)"></component>
          {{ currentLanguage.name.toUpperCase().substring(0, 2) }}
          </button>
          <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="dropdownLanguageButton">
            <li v-for="language in supportedLanguages" :key="language.code">
              <span class="dropdown-item" @click="changeLocale(language.code)">
                <component :is="getFlagComponent(language.flag)"></component>&nbsp; {{ language.nativeName }}
              </span>
            </li>
          </ul>
          </div>
      </div>
      <div class="card">
        <form class="needs-validation" @submit.prevent="register">
          <h4 class="card-header"><span v-t="'message.Login.create_account'"></span></h4>
          <div class="card-body">
            <div class="vstack gap-2 mb-4" v-if="!success">
              <div class="form-floating">
                <input
                  type="text"
                  class="form-control form-control-lg"
                  id="firstName"
                  placeholder="First name"
                  required
                  v-model="account.firstname"
                />
                <label for="firstName" v-t="'message.AccountSettings.first_name'"></label>
                <div class="invalid-tooltip">Validation Error Message</div>
              </div>
              <div class="form-floating">
                <input
                  type="text"
                  class="form-control form-control-lg"
                  id="lastName"
                  placeholder="Last name"
                  required
                  v-model="account.lastname"
                />
                <label for="lastName" v-t="'message.AccountSettings.last_name'"></label>
                <div class="invalid-tooltip">Validation Error Message</div>
              </div>

              <div class="form-floating">
                <input
                  type="email"
                  class="form-control form-control-lg"
                  id="email"
                  placeholder="Email"
                  required
                  v-model="account.email"
                />
                <label for="email" v-t="'message.Login.enter_email'"></label>
                <div class="invalid-tooltip">Validation Error Message</div>
              </div>
              <div class="form-floating input-group input-group-end">
                <input
                  :type="[cleartextPass1 ? 'text' : 'password']"
                  class="form-control form-control-lg"
                  id="password1"
                  placeholder="Choose Password"
                  required
                  v-model="account.password"
                />
                <label for="password1" v-t="'message.Login.password'"></label>
                <button
                  class="btn btn-lg"
                  @click="cleartextPass1 = !cleartextPass1"
                  :title="[cleartextPass1 ? 'Hide Password' : 'Show Password']"
                >
                  <i
                    class="bi text-primary"
                    :class="[cleartextPass1 ? 'bi-eye-fill' : 'bi-eye-slash-fill']"
                  ></i>
                </button>
                <div class="invalid-tooltip">Validation Error Message</div>
              </div>
              <div class="form-floating input-group input-group-end">
                <input
                  :type="[cleartextPass2 ? 'text' : 'password']"
                  class="form-control form-control-lg"
                  id="password2"
                  placeholder="Confirm Password"
                  required
                  v-model="account.confirmPassword"
                />
                <label for="password2" v-t="'message.Login.confirm_password'"></label>
                <button
                  class="btn btn-lg"
                  @click="cleartextPass2 = !cleartextPass2"
                  :title="[cleartextPass2 ? 'Hide Password' : 'Show Password']"
                >
                  <i
                    class="bi text-primary"
                    :class="[cleartextPass2 ? 'bi-eye-fill' : 'bi-eye-slash-fill']"
                  ></i>
                </button>
                <div class="invalid-tooltip">Validation Error Message</div>
              </div>
            </div>

            <button
              class="submit"
              @submit.prevent="register"
              :disabled="loadingState"
              v-if="!success"
            >
              <span v-t="'message.Login.create_my_account'"></span>
            </button>
            <div class="alert alert-danger px-5 mt-2" v-if="failed">
              Account creation failed. 
              <span v-if="registration_response.error_code == 409">
               There is an existing account with that email address. Try using "Forgot Password" to reset your password or contact your school for help.
              </span>
              <span v-if="registration_response.error_code == 406">
                {{ registration_response.message }}
                Please try again or contact your school for help.
              </span>
              
            </div>
            <div class="alert alert-success px-5 mt-2" v-if="success">
              Congratulations creation passed. Please check your email to proceed.
            </div>
          </div>
          <div class="card-footer">
            <router-link
              class="btn btn-link"
              :to="{
                name: 'parent-login',
                params: { account: schoolStore.school?.subdomain }
              }"
            >
              <i class="bi bi-arrow-left me-2 opacity-50"></i><span v-t="'message.Login.return_to_sign_in'"></span>
            </router-link>
          </div>
        </form>
      </div>
      <small class="text-muted vstack gap-1">
        <strong class="opacity-50">Parent Teacher Conference Scheduling Made Easy</strong>
        <div>&copy;2006 - {{ new Date().getFullYear() }} PTC Wizard, All rights reserved.</div>
      </small>
    </section>
  </main>
  <main v-else class="login loading-splash">
    <LoadingIcon />
    <span>Loading School Data…..</span>
  </main>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import AuthService from "@/services/auth.service";
import { useRouter } from "vue-router";
import { useRoute } from "vue-router";
import Logo from "@/assets/icons/logo-vertical-regular.svg";
import LoadingIcon from "@/assets/icons/loading.svg";
import { useSchoolStore } from "@/stores/school";
import { useAuthenticationStore } from "@/stores/authentication";
// Flag imports
import FlagUS from "@/assets/icons/flags/usa.svg";
import FlagES from "@/assets/icons/flags/es.svg";
import FlagFR from "@/assets/icons/flags/fr.svg";
import FlagDE from "@/assets/icons/flags/de.svg";
import FlagAR from "@/assets/icons/flags/ar.svg";
import FlagCN from "@/assets/icons/flags/cn.svg";
import FlagJP from "@/assets/icons/flags/jp.svg";
import FlagBD from "@/assets/icons/flags/bd.svg";
import FlagKR from "@/assets/icons/flags/kr.svg";
import FlagPK from "@/assets/icons/flags/pk.svg";
import FlagSN from "@/assets/icons/flags/sn.svg";
import { useLanguage } from "@/composables/useLanguage.js";
const authenticationStore = useAuthenticationStore();
const schoolStore = useSchoolStore();
const router = useRouter();
const route = useRoute();
const { currentLanguage, supportedLanguages, changeLocale } = useLanguage();

// Flag component mapping
const flagComponents = {
  'usa': FlagUS,
  'es': FlagES,
  'fr': FlagFR,
  'de': FlagDE,
  'ar': FlagAR,
  'cn': FlagCN,
  'jp': FlagJP,
  'bd': FlagBD,
  'kr': FlagKR,
  'pk': FlagPK,
  'sn': FlagSN
};

function getFlagComponent(flagKey) {
  return flagComponents[flagKey] || FlagUS;
}

const account = ref({});
const loadingState = ref(false);
const cleartextPass1 = ref(false);
const cleartextPass2 = ref(false);
const toggleFail = ref(false);
const toggleSubmit = ref(false);
const success = ref(false);
const failed = ref(false);
const registration_response = ref({});
function register() {
  loadingState.value = true;
  // toggleValidation.value = true;
  // toggleSubmit.value = true;
  const user = {
    authentication_type: "parent",
    username: account.value.email,
    email: account.value.email,
    firstname: account.value.firstname,
    lastname: account.value.lastname,
    password: account.value.password,
    account: schoolStore.school.subdomain,
    school_id: schoolStore.school.school_id,
    district_id: schoolStore.school.district_id
  };
  AuthService.register(user).then((response) => {
    loadingState.value = false;

    if (response && response.status) {
      success.value = true;
      failed.value = false;
      console.log("Registration Successful");
      authenticationStore.login(user).then((response) => {
        if (response) {
          console.log("Login Successful");
          // authenticationStore.fetchProfile().then((response) => {
          router.push({
            name: "parent-list"
          });
          // });
        } else {
          console.log("Login Failed");
          toggleFail.value = true;
          toggleSubmit.value = false;
        }
      });
    } else {
      console.log("Registration Failed");
      console.log(response);
      registration_response.value=response;
      success.value = false;
      failed.value = true;
    }
  });
}
onMounted(() => {
  if (schoolStore.school?.school_id) {
    return true;
  } else {
    if (route.params.account != null && route.params.account != "") {
      schoolStore.fetchSchool(route.params.account).then(() => {
        console.log("School found");
      });
    } else {
      window.location.href = "https://www.ptcwizard.com";
    }
  }
});
</script>
